<template>
<div class="chapter">
<div class="inner_page">
<div>
<h2 class="chaptertitle"><b>Introduction</b></h2>
<p class="body-text"><big><big>T</big></big>his book is a collection of nineteenth- and twentieth-century recipes from the University of Kentucky Libraries Special Collections. Archival collections were scoured for clues that would lead us to finding recipes: journals, scrapbooks, and books of “receipts”—an old term for “recipe.” Most of the recipes included in this book are adapted from handwritten recipes in the archives, many are from clippings pasted in scrapbooks, and some are from historic Kentucky cookbooks. We thought it was important to focus on the handwritten recipes; they seemed more cherished, more likely to have been used to prepare food in Kentucky homes, and more traditional or authentic to Kentucky's food history. We hope we are giving these rediscovered historic recipes new life and the chance to be passed down to future generations.</p>
<p class="indent">Because the two of us share a passion for food and Kentucky history, there was a natural connection that led to creating a historical cookbook. One thing that sets this cookbook apart from others is that all of the recipes included have been tested, and many of them retested. With few exceptions, the recipes are not modified to alter the taste or core values of the dish. Whenever possible we obtained the ingredients locally. Most recipe modifications were substitutions for a fat or for a rare or extinct ingredient, and these substitutions are indicated throughout the book. The most significant addition is that we supplied notes on preparation, cooking times, and oven temperature, because few of the early originals contained these procedures. Although we want to convey a sense of the original cooking environment, we've done everything possible to make the recipes user-friendly for modern home cooks.</p>
<p class="indent">These recipes were hand-selected based on their timelessness and our personal tastes, to represent the culinary diversity of the area and fulfill our desire to preserve Kentucky history through regional recipes. Our Kentucky heritage is unique, and we want to celebrate and preserve it by sharing recipes from the collective memories of our elders. These recipes carry the aura of a different time and a slower pace of life, in which meals were handmade and shared. It is this ritual, by means of these recipes, that we hope to preserve.</p>
<p class="indent">Most of these early American cooks (or collectors) preserved their recipes in handwritten volumes, and few of them have been published. Here you will see a mixture of African, English, French, American, and southern flavors. Perhaps the desire to create this cookbook comes from our inborn southern drive for hospitality—to serve and share good food with family, friends, and even strangers. In any case, this cookbook will help to keep the durable southern tradition alive with recipes from our collective history.</p>
<p class="indent">When a person's name appears in a recipe title, it is the individual who compiled or collected the recipe. Some dishes may be credited to another, but like the recipes in your kitchen, they may have been handed down from family or friends, clipped from a newspaper, or shared by neighbors. These are titled only to show who <i>collected</i> them, not to indicate who made them distinctive, local, or even southern.</p>
<p class="indent">Since many of these handwritten recipes did not use standardized measurements, the following will help if you should decide to try cooking your own dishes from historic texts.</p>
<p class="img"><img alt="image" src="images/3.jpg" style="height: auto; width: auto; margin:0px 0px 0px 0px; padding:0px 0px 0px 0px; border:0px;"/></p>
<p class="figcaption">Two women and a man preparing food for a surveying class, circa 1930s–1940s. Louis Edward Nollau F Series Photographic Print Collection, University of Kentucky Libraries.</p>
<h3 class="subhead2"><b>Measurements</b></h3>
<table class="tab" width="100%">
<colgroup><col width="57%"/>
<col width="43%"/>
</colgroup><tbody><tr>
<td align="left" valign="top">A pinch or dash</td>
<td align="left" valign="top">roughly ⅛ teaspoon</td>
</tr>
<tr>
<td align="left" valign="top">1 kitchen spoon</td>
<td align="left" valign="top">1 teaspoon</td>
</tr>
<tr>
<td align="left" valign="top">1 dessert or soup spoon</td>
<td align="left" valign="top">2 teaspoons</td>
</tr>
<tr>
<td align="left" valign="top">1 spoonful</td>
<td align="left" valign="top">1 tablespoon</td>
</tr>
<tr>
<td align="left" valign="top">Butter the size of a walnut</td>
<td align="left" valign="top">roughly 2 tablespoons</td>
</tr>
<tr>
<td align="left" valign="top">Butter the size of a hen's egg</td>
<td align="left" valign="top">3 to 4 tablespoons</td>
</tr>
<tr>
<td align="left" valign="top">1 pound of flour</td>
<td align="left" valign="top">approximately 4 cups</td>
</tr>
<tr>
<td align="left" valign="top">1 pound of sugar</td>
<td align="left" valign="top">approximately 2 cups</td>
</tr>
<tr>
<td align="left" valign="top">1 pound of eggs</td>
<td align="left" valign="top">8 with their shells, 10 without</td>
</tr>
<tr>
<td align="left" valign="top">1 gill</td>
<td align="left" valign="top">4 fluid ounces or ½ cup</td>
</tr>
<tr>
<td align="left" valign="top">1 teacup</td>
<td align="left" valign="top">6 fluid ounces or ¾ cup</td>
</tr>
<tr>
<td align="left" valign="top">1 wine glass</td>
<td align="left" valign="top">2 fluid ounces or ¼ cup</td>
</tr>
<tr>
<td align="left" valign="top">1 tumbler</td>
<td align="left" valign="top">8 fluid ounces or 1 cup</td>
</tr>
<tr>
<td align="left" valign="top">1 coffee cup</td>
<td align="left" valign="top">nearly 8 fluid ounces</td>
</tr>
</tbody></table>
<h3 class="subhead2"><b>Milk Substitutions</b></h3>
<table class="tab" width="100%">
<colgroup><col width="57%"/>
<col width="43%"/>
</colgroup><tbody><tr>
<td align="left" valign="top">Sweet milk</td>
<td align="left" valign="top">milk that has not soured; use whole milk</td>
</tr>
<tr>
<td align="left" valign="top">Sweet cream</td>
<td align="left" valign="top">use heavy cream</td>
</tr>
<tr>
<td valign="top">Sour milk</td>
<td valign="top">milk that has soured; while buttermilk is not quite the same, it can be used. Or sour whole milk by adding 1 tablespoon of lemon juice per cup of milk.</td>
</tr>
<tr>
<td align="left" valign="top">Milk</td>
<td align="left" valign="top">Whole milk</td>
</tr>
</tbody></table>
<h3 class="subhead2"><b>Oven Temperatures (Fahrenheit)</b></h3>
<table class="tab" width="100%">
<colgroup><col width="57%"/>
<col width="43%"/>
</colgroup><tbody><tr>
<td align="left" valign="top">Slow</td>
<td align="left" valign="top">300 degrees</td>
</tr>
<tr>
<td align="left" valign="top">Moderate</td>
<td align="left" valign="top">350 degrees</td>
</tr>
<tr>
<td align="left" valign="top">Quick</td>
<td align="left" valign="top">375 to 400 degrees</td>
</tr>
<tr>
<td align="left" valign="top">Hot</td>
<td align="left" valign="top">400 to 425 degrees</td>
</tr>
</tbody></table>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// intro.vue?29eefe32